/**
 * Schema.org Structured Data for CAF Group of Companies
 * Pre-built schemas for LocalBusiness, Service, Organization, etc.
 */

const SITE_URL = 'https://www.cafinc.ca';

// Shared address for both companies
const sharedAddress = {
  '@type': 'PostalAddress',
  'streetAddress': '116-25 Belich Crescent',
  'addressLocality': 'Red Deer County',
  'addressRegion': 'AB',
  'postalCode': 'T4S 2K5',
  'addressCountry': 'CA',
};

const sharedGeo = {
  '@type': 'GeoCoordinates',
  'latitude': 52.2681,
  'longitude': -113.8112,
};

const serviceArea = {
  '@type': 'GeoCircle',
  'geoMidpoint': sharedGeo,
  'geoRadius': '100000', // 100km radius
};

// CAF Group Parent Organization
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  'name': 'CAF Group of Companies',
  'alternateName': 'CAF Inc',
  'url': SITE_URL,
  'logo': `${SITE_URL}/logo-color.png`,
  'description': 'CAF Group of Companies - Central Alberta Fencing & Property Services. Quality fencing installation and commercial property maintenance.',
  'address': sharedAddress,
  'telephone': '+1-866-986-5484',
  'email': 'hello@cafinc.ca',
  'sameAs': [],
  'areaServed': {
    '@type': 'State',
    'name': 'Alberta',
    'containedInPlace': {
      '@type': 'Country',
      'name': 'Canada',
    },
  },
};

// Central Alberta Fencing LocalBusiness
export const fencingBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/fence#business`,
  'name': 'Central Alberta Fencing Inc',
  'alternateName': 'CAF Fencing',
  'description': 'Professional fence supply and installation services in Central Alberta. Specializing in vinyl, wood, chain link, and ornamental fencing for residential, commercial, and agricultural properties.',
  'url': `${SITE_URL}/fence`,
  'logo': `${SITE_URL}/logo-fence.png`,
  'image': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&h=630&fit=crop',
  'telephone': '+1-403-986-5484',
  'email': 'fence@cafinc.ca',
  'address': sharedAddress,
  'geo': sharedGeo,
  'areaServed': serviceArea,
  'priceRange': '$$',
  'openingHoursSpecification': [
    {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      'opens': '08:00',
      'closes': '16:30',
    },
  ],
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '5.0',
    'reviewCount': '47',
    'bestRating': '5',
    'worstRating': '1',
  },
  'hasOfferCatalog': {
    '@type': 'OfferCatalog',
    'name': 'Fencing Services',
    'itemListElement': [
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Vinyl Fencing',
          'description': 'Premium VEKA vinyl fence supply and installation',
        },
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Wood Fencing',
          'description': 'Pressure treated wood fence installation',
        },
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Chain Link Fencing',
          'description': 'Residential and commercial chain link fencing',
        },
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Ornamental Fencing',
          'description': 'Decorative iron and aluminum fencing',
        },
      },
    ],
  },
  'parentOrganization': {
    '@type': 'Organization',
    'name': 'CAF Group of Companies',
    'url': SITE_URL,
  },
};

// CAF Property Services LocalBusiness
export const propertyServicesBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/ps#business`,
  'name': 'CAF Property Services',
  'alternateName': 'CAF PS',
  'description': 'Commercial exterior property maintenance services in Central Alberta. Including landscape maintenance, parking lot repair, snow removal, signage, and more.',
  'url': `${SITE_URL}/ps`,
  'logo': `${SITE_URL}/logo-ps.png`,
  'image': 'https://images.unsplash.com/photo-1616128025408-901b4af64d13?w=1200&h=630&fit=crop',
  'telephone': '+1-403-986-5484',
  'email': 'ps@cafinc.ca',
  'address': sharedAddress,
  'geo': sharedGeo,
  'areaServed': serviceArea,
  'priceRange': '$$',
  'openingHoursSpecification': [
    {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      'opens': '08:00',
      'closes': '16:30',
    },
    {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': ['Saturday', 'Sunday'],
      'description': '24/7 Emergency Snow Removal Available',
    },
  ],
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': '5.0',
    'reviewCount': '32',
    'bestRating': '5',
    'worstRating': '1',
  },
  'hasOfferCatalog': {
    '@type': 'OfferCatalog',
    'name': 'Property Services',
    'itemListElement': [
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Landscape Maintenance',
          'description': 'Commercial landscape upkeep and maintenance',
        },
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Snow Removal',
          'description': '24/7 emergency snow removal services',
        },
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Parking Lot Repair',
          'description': 'Asphalt repair and maintenance',
        },
      },
      {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Signage',
          'description': 'Commercial signage installation and repair',
        },
      },
    ],
  },
  'parentOrganization': {
    '@type': 'Organization',
    'name': 'CAF Group of Companies',
    'url': SITE_URL,
  },
};

// Service schema generator for individual service pages
export const createServiceSchema = ({
  name,
  description,
  url,
  image,
  provider = 'fence', // 'fence' or 'property'
  category,
}) => {
  const providerSchema = provider === 'fence' ? fencingBusinessSchema : propertyServicesBusinessSchema;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': name,
    'description': description,
    'url': `${SITE_URL}${url}`,
    'image': image,
    'provider': {
      '@type': 'LocalBusiness',
      'name': providerSchema.name,
      'url': providerSchema.url,
    },
    'areaServed': serviceArea,
    'category': category,
    'serviceType': name,
  };
};

// FAQ schema generator
export const createFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqs.map(faq => ({
    '@type': 'Question',
    'name': faq.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': faq.answer,
    },
  })),
});

// Review schema generator
export const createReviewSchema = (reviews, businessType = 'fence') => {
  const business = businessType === 'fence' ? fencingBusinessSchema : propertyServicesBusinessSchema;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': business.name,
    'review': reviews.map(review => ({
      '@type': 'Review',
      'author': {
        '@type': 'Person',
        'name': review.name,
      },
      'reviewRating': {
        '@type': 'Rating',
        'ratingValue': review.rating,
        'bestRating': '5',
        'worstRating': '1',
      },
      'reviewBody': review.text,
      'datePublished': review.date,
    })),
  };
};

// Fence service page schemas
export const vinylFenceSchema = createServiceSchema({
  name: 'Vinyl Fencing Installation',
  description: 'Premium VEKA vinyl fence supply and installation in Central Alberta. Low maintenance, durable, and beautiful vinyl fencing for residential and commercial properties.',
  url: '/fence/vinyl',
  image: 'https://images.unsplash.com/photo-1656533391116-0169a27cbfaf?w=1200&h=630&fit=crop',
  provider: 'fence',
  category: 'Fencing Installation',
});

export const woodFenceSchema = createServiceSchema({
  name: 'Wood Fencing Installation',
  description: 'Quality pressure treated wood fence installation in Central Alberta. Cedar and spruce options for privacy fences, picket fences, and more.',
  url: '/fence/wood',
  image: 'https://images.unsplash.com/photo-1608144029870-78ae64886113?w=1200&h=630&fit=crop',
  provider: 'fence',
  category: 'Fencing Installation',
});

export const chainLinkFenceSchema = createServiceSchema({
  name: 'Chain Link Fencing Installation',
  description: 'Durable chain link fence installation for residential, commercial, and industrial properties in Central Alberta. Galvanized and vinyl-coated options available.',
  url: '/fence/chain-link',
  image: 'https://images.unsplash.com/photo-1642376426526-29c4ec9363ee?w=1200&h=630&fit=crop',
  provider: 'fence',
  category: 'Fencing Installation',
});

export const ornamentalFenceSchema = createServiceSchema({
  name: 'Ornamental Fencing Supply',
  description: 'Premium ornamental iron and aluminum fence supply in Central Alberta. Authorized dealer for Direct Fencing Supply and Ameristar Perimeter Security. Montage, Echelon, and Aegis product lines. Atlas gate hardware. Made in USA options with 20-year warranty.',
  url: '/fence/ornamental',
  image: 'https://images.unsplash.com/photo-1774343680173-cbcbfa15322d?w=1200&h=630&fit=crop',
  provider: 'fence',
  category: 'Fencing Supply',
});

// Property service page schemas
export const snowRemovalSchema = createServiceSchema({
  name: 'Commercial Snow Removal',
  description: '24/7 emergency commercial snow removal services in Central Alberta. Reliable snow plowing, salting, and ice management for businesses.',
  url: '/ps/snow-removal',
  image: 'https://images.unsplash.com/photo-1616128025408-901b4af64d13?w=1200&h=630&fit=crop',
  provider: 'property',
  category: 'Property Maintenance',
});

export const landscapeSchema = createServiceSchema({
  name: 'Commercial Landscape Maintenance',
  description: 'Professional commercial landscape maintenance in Central Alberta. Lawn care, garden maintenance, and property beautification services.',
  url: '/ps/landscape-maintenance',
  image: 'https://images.unsplash.com/photo-1661133732576-6140941d5421?w=1200&h=630&fit=crop',
  provider: 'property',
  category: 'Property Maintenance',
});

export const parkingLotSchema = createServiceSchema({
  name: 'Parking Lot Repair',
  description: 'Commercial parking lot repair and asphalt maintenance in Central Alberta. Pothole repair, crack sealing, and line painting services.',
  url: '/ps/parking-lot-repair',
  image: 'https://images.unsplash.com/photo-1697813165600-36bdc8f2ef7e?w=1200&h=630&fit=crop',
  provider: 'property',
  category: 'Property Maintenance',
});
